import { Box, Button, Collapse, Grid, Pagination, Paper } from "@mui/material"
import { useSnackbar } from "notistack"
import React from "react"
import { ApiError } from "../../Api/ApiError"
import { getLessonUnreservableList, LessonUnreservables, editLessonUnreservable, addLessonUnreservable } from "../../Api/OnlineLesson"
import { Time } from "../../Helper/Time"
import { LessonUnreservable } from "../../Objects/OnlineLesson"
import CategoryTitle from "../atoms/CategoryTitle"
import LessonunReservableInputForm from "../molecules/LessonUnreservableInptForm"
import LessonUnreservableItem from "../molecules/LessonUnreservableItem"

export interface LessonUnreservableListProps {
    teacher_id: number
    display_rows: number
}
const LessonUnreservableList: React.FC<LessonUnreservableListProps> = ({
    teacher_id,
    display_rows,
}) => {
    let [lessonUnreservables, setLessonUnreservables] = React.useState<LessonUnreservables>()
    let [pagenum, setPagenum] = React.useState(1)


    let [formOpen, setFormOpen] = React.useState(false)
    let [addLurvableDate, setAddLurvableDate] = React.useState<Date>()
    let [addStartTime, setAddStartTime] = React.useState<Time>(new Time("00:00"))
    let [addEndTime, setAddEndTime] = React.useState<Time>(new Time("30:00"))
    let [addEnableTimeRange, setAddEnableTimeRange] = React.useState(false)
    let [editLurvableId, setEditLurvableId] = React.useState<number>()
    let { enqueueSnackbar } = useSnackbar()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    let loadData = React.useCallback((pagenum: number) => {
        getLessonUnreservableList({
            limit: display_rows,
            offset: (pagenum - 1) * display_rows,
            teacher_id: teacher_id
        }, {signal: abortController.signal}).then(lessonUnreservables => {
            setLessonUnreservables(lessonUnreservables)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, teacher_id, display_rows, enqueueSnackbar])

    const doEditLessonUnreservable = React.useCallback(() => {
        if (addLurvableDate) {
            if (editLurvableId) {
                let startTime: Time | undefined = undefined
                let endTime: Time | undefined = undefined
                if (addEnableTimeRange && (addStartTime.toSec() > 0 || addEndTime.toSec() < 30 * 60 * 60)) {
                    startTime = addStartTime
                    endTime = addEndTime
                }
                editLessonUnreservable({
                    lurvable_id: editLurvableId,
                    teacher_id: teacher_id,
                    lurvable_date: addLurvableDate,
                    start: startTime,
                    end: endTime
                }, {signal: abortController.signal}).then(() => {
                    enqueueSnackbar("変更しました", { variant: "success" })
                    setFormOpen(false)
                    loadData(pagenum)
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
            } else {
                let startTime: Time | undefined = undefined
                let endTime: Time | undefined = undefined
                if (addEnableTimeRange && (addStartTime.toSec() > 0 || addEndTime.toSec() < 30 * 60 * 60)) {
                    startTime = addStartTime
                    endTime = addEndTime
                }
                addLessonUnreservable({
                    teacher_id: teacher_id,
                    lurvable_date: addLurvableDate,
                    start: startTime,
                    end: endTime
                }, {signal: abortController.signal}).then(lurvable_id => {
                    enqueueSnackbar("登録しました", { variant: "success" })
                    setFormOpen(false)
                    loadData(pagenum)
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
            }
        } else {
            enqueueSnackbar("日付を選択してください", { variant: "error" })
        }
    }, [abortController, addLurvableDate, editLurvableId, addEnableTimeRange, addStartTime, addEndTime, teacher_id, enqueueSnackbar, loadData, pagenum])

    /**
     * フォームデータの初期化
     */
    let initFormValues = React.useCallback(({
        lurvableId,
        lurvableDate,
        startTime,
        endTime,
    }: {
        lurvableId?: number,
        lurvableDate?: Date,
        startTime?: Time,
        endTime?: Time,
    }) => {
        setAddLurvableDate(lurvableDate)
        setEditLurvableId(lurvableId)
        if (startTime && endTime) {
            setAddEnableTimeRange(startTime.toSec() > 0 || endTime.toSec() < 30 * 60 * 60)
        } else {
            setAddEnableTimeRange(false)
        }
        setAddStartTime(startTime ?? new Time("00:00"))
        setAddEndTime(endTime ?? new Time("30:00"))
    }, [])

    /**
     * ページ変更時のデータ取得処理
     */
    React.useEffect(() => {
        loadData(pagenum)
    }, [pagenum, loadData])

    return (
        <Paper>
            <CategoryTitle text="予約不可範囲設定" />
            <Box
                sx={{
                    padding: '10px'
                }}
            >
                <Collapse
                    in={formOpen}
                    onExited={() => {
                        initFormValues({})
                    }}
                >
                    <LessonunReservableInputForm
                        onChange={(lurvable_date: Date | null | undefined, start: Time, end: Time) => {
                            setAddLurvableDate(lurvable_date ? lurvable_date : undefined)
                            setAddStartTime(start)
                            setAddEndTime(end)
                        }}
                        onChangeEnableTimeRange={enableTimeRange => {
                            setAddEnableTimeRange(enableTimeRange)
                        }}
                        enableTimeRange={addEnableTimeRange}
                        lurvableId={editLurvableId}
                        lurvable_date={addLurvableDate}
                        startTime={addStartTime}
                        endTime={addEndTime}
                        timeRangeFrom={new Time("00:00")}
                        timeRangeTo={new Time("30:00")}
                        timeRangeUnit={30}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'right'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFormOpen(false)
                            }}
                            sx={{
                                margin: "0 10px"
                            }}
                        >キャンセル</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                doEditLessonUnreservable()
                            }}
                            sx={{
                                margin: "0 10px"
                            }}
                        >{editLurvableId ? "変更" : "追加"}</Button>
                    </Box>
                </Collapse>
                <Collapse
                    in={!formOpen}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'right'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFormOpen(true)
                            }}
                        >追加</Button>
                    </Box>
                </Collapse>
            </Box>

            <Box sx={{
                padding: "10px"
            }}>
                <Grid container spacing={2} alignItems="stretch">
                    {lessonUnreservables &&
                        lessonUnreservables.list.map((lessonUnreservable: LessonUnreservable, index: number) => {
                            return (
                                <Grid item key={teacher_id + '-' + index} xs={12} sm={6} md={4}>
                                    <LessonUnreservableItem
                                        lessonUnreservable={lessonUnreservable}
                                        // onDelete={requestDelete ? () => {
                                        //     requestDelete(lessonUnreservable)
                                        // } : undefined}
                                        onEdit={() => {
                                            initFormValues({
                                                lurvableId: lessonUnreservable.lurvable_id,
                                                lurvableDate: lessonUnreservable.lurvable_date,
                                                startTime: lessonUnreservable.start ? lessonUnreservable.start : undefined,
                                                endTime: lessonUnreservable.end ? lessonUnreservable.end : undefined,
                                            })
                                            setFormOpen(true)
                                        }}
                                    />
                                </Grid>
                            )
                        })
                    }
                </Grid>
            </Box>
            {lessonUnreservables &&
                <Box sx={{
                    padding: "10px",
                    display: "flex",
                    justifyContent: "center",
                }}>
                    <Pagination
                        count={lessonUnreservables.pagenum}
                        onChange={(e, page) => {
                            setPagenum(page)
                        }}
                        variant="outlined"
                    />
                </Box>
            }
        </Paper>
    )
}


export default LessonUnreservableList